import type {PopupAttrs} from '@jetbrains/ring-ui/components/popup/popup'
import {Directions} from '@jetbrains/ring-ui/components/popup/popup.consts'
import classNames from 'classnames'
import type {ReactNode} from 'react'
import * as React from 'react'
import {useEffect} from 'react'

import useEventCallback from '../../../../hooks/useEventCallback'
import {getSandbox} from '../../../../utils/fastdom'
import Popup from '../../Popup/Popup.lazy'

import styles from './VirtualizedTreeOverlayItem.css'

const popupDirections = [Directions.TOP_RIGHT]

interface Props {
  className: string
  height: number
  width: number
  popupHtmlProps?: Partial<PopupAttrs>
  children: ReactNode
  onClose: () => void
}

function VirtualizedTreeOverlayItemPopupComponent(props: Props) {
  const {className, height, width, popupHtmlProps, children, onClose} = props
  const popupRef = React.useRef<HTMLDivElement>(null)

  const onMouseMove = useEventCallback(async (event: MouseEvent) => {
    if (popupRef.current != null) {
      const target = popupRef.current
      const sandbox = getSandbox(target)

      const isOutOfElement = await sandbox.measure(() => {
        const {top, bottom, right} = target.getBoundingClientRect()

        return top > 0 && (event.clientY < top || event.clientY > bottom || event.clientX > right)
      })

      if (isOutOfElement) {
        onClose()
      }
    }
  })

  useEffect(() => {
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('wheel', onClose)
    document.body?.addEventListener('mouseleave', onClose)

    return () => {
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('wheel', onClose)
      document.body?.removeEventListener('mouseleave', onClose)
    }
  })

  return (
    <>
      <Popup
        className={classNames(styles.popup, styles.nonInteractivePopup)}
        top={height}
        left={0}
        directions={popupDirections}
        minWidth={width}
        autoCorrectTopOverflow={false}
        {...popupHtmlProps}
      >
        <div className={className}>{children}</div>
      </Popup>
      <Popup
        className={classNames(styles.popup, styles.interactivePopup)}
        top={height}
        left={0}
        directions={popupDirections}
        minWidth={width}
        autoCorrectTopOverflow={false}
        {...popupHtmlProps}
      >
        <div className={className} ref={popupRef}>
          {children}
        </div>
      </Popup>
    </>
  )
}

export const VirtualizedTreeOverlayItemPopup = React.memo(VirtualizedTreeOverlayItemPopupComponent)
